// src/components/DocFinalizeModal.jsx
import React, { useEffect, useMemo, useState } from "react";

/**
 * DocFinalizeModal
 *
 * In-app final review dialog. This component does not call the backend directly.
 * It asks the parent to:
 *
 * - prepare the exact PDF preview;
 * - finalize the selected revision in the selected output format.
 *
 * The canonical PDF is always produced. Selecting Word or Text requests the PDF
 * plus that companion file.
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - onPreviewPdf?: () => Promise<void> | void
 *  - onFinalizeAndExport: (
 *      format: "pdf" | "docx" | "text"
 *    ) => Promise<void> | void
 *  - defaultFormat?: "pdf" | "docx" | "text"
 *  - subject?: string
 */
const DocFinalizeModal = ({
  open,
  onClose,
  onPreviewPdf,
  onFinalizeAndExport,
  defaultFormat = "pdf",
  subject = "",
}) => {
  const normalizedDefaultFormat = ["pdf", "docx", "text"].includes(
    defaultFormat
  )
    ? defaultFormat
    : "pdf";

  const [format, setFormat] = useState(normalizedDefaultFormat);
  const [reviewConfirmed, setReviewConfirmed] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [previewMessage, setPreviewMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const busy = previewing || finalizing;

  const formatOptions = useMemo(
    () => [
      {
        value: "pdf",
        title: "PDF",
        shortTitle: "PDF",
        description:
          "Create the final locked PDF for viewing, printing, and sharing.",
        badge: "Default",
        tone: "#1E2A78",
        soft: "#EEF2FF",
        border: "#A5B4FC",
      },
      {
        value: "docx",
        title: "PDF + Word",
        shortTitle: "PDF + Word",
        description:
          "Create the final PDF plus a downloadable Microsoft Word file.",
        badge: "Two files",
        tone: "#2563EB",
        soft: "#EFF6FF",
        border: "#93C5FD",
      },
      {
        value: "text",
        title: "PDF + Text",
        shortTitle: "PDF + Text",
        description:
          "Create the final PDF plus a plain-text companion file.",
        badge: "Two files",
        tone: "#7C3AED",
        soft: "#F5F3FF",
        border: "#C4B5FD",
      },
    ],
    []
  );

  const selectedOption =
    formatOptions.find((option) => option.value === format) ||
    formatOptions[0];

  useEffect(() => {
    if (!open) return;

    setFormat(normalizedDefaultFormat);
    setReviewConfirmed(false);
    setPreviewing(false);
    setFinalizing(false);
    setPreviewMessage("");
    setErrorMessage("");
  }, [open, normalizedDefaultFormat]);

  useEffect(() => {
    if (!open || typeof document === "undefined") return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  useEffect(() => {
    if (!open || typeof window === "undefined") return undefined;

    const handleKeyDown = (event) => {
      if (event.key !== "Escape" || busy) return;

      if (typeof onClose === "function") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, busy, onClose]);

  if (!open) return null;

  const handleBackdropClick = (event) => {
    if (event.target !== event.currentTarget || busy) return;

    if (typeof onClose === "function") {
      onClose();
    }
  };

  const handlePreview = async () => {
    if (busy || typeof onPreviewPdf !== "function") return;

    setPreviewing(true);
    setErrorMessage("");
    setPreviewMessage("");

    try {
      await onPreviewPdf();
      setPreviewMessage(
        "PDF preview opened. Review the complete document, then return here for the final confirmation."
      );
    } catch (error) {
      setErrorMessage(
        error?.message ||
          "The PDF preview could not be prepared. Please try again."
      );
    } finally {
      setPreviewing(false);
    }
  };

  const handleFinalize = async () => {
    if (
      busy ||
      !reviewConfirmed ||
      typeof onFinalizeAndExport !== "function"
    ) {
      return;
    }

    setFinalizing(true);
    setErrorMessage("");

    try {
      await onFinalizeAndExport(format);
    } catch (error) {
      setErrorMessage(
        error?.message ||
          "The document could not be finalized. Please try again."
      );
      setFinalizing(false);
    }
  };

  const finalizeLabel =
    format === "docx"
      ? "Finalize PDF + Word"
      : format === "text"
      ? "Finalize PDF + Text"
      : "Finalize PDF";

  const styles = {
    backdrop: {
      position: "fixed",
      inset: 0,
      zIndex: 1100,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      overflowY: "auto",
      padding: 18,
      boxSizing: "border-box",
      background: "rgba(15, 23, 42, 0.54)",
      backdropFilter: "blur(4px)",
      WebkitBackdropFilter: "blur(4px)",
    },

    card: {
      width: "min(640px, 100%)",
      maxHeight: "calc(100dvh - 36px)",
      margin: "auto",
      overflowY: "auto",
      overscrollBehavior: "contain",
      border: "1px solid rgba(15, 23, 42, 0.12)",
      borderRadius: 22,
      background: "#FFFFFF",
      color: "#0F172A",
      boxShadow:
        "0 30px 80px rgba(15, 23, 42, 0.28), 0 8px 24px rgba(15, 23, 42, 0.14)",
    },

    header: {
      display: "grid",
      gridTemplateColumns: "46px minmax(0, 1fr) auto",
      gap: 13,
      alignItems: "start",
      padding: "20px 22px 17px",
      borderBottom: "1px solid #E2E8F0",
      background:
        "linear-gradient(135deg, rgba(30,42,120,0.07), rgba(15,118,110,0.04))",
    },

    lockIcon: {
      width: 44,
      height: 44,
      borderRadius: 14,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "#1E2A78",
      color: "#FFFFFF",
      fontSize: 21,
      fontWeight: 900,
      boxShadow: "0 8px 18px rgba(30, 42, 120, 0.22)",
    },

    headerText: {
      minWidth: 0,
    },

    eyebrow: {
      marginBottom: 4,
      color: "#1E2A78",
      fontSize: 11,
      lineHeight: 1.2,
      fontWeight: 900,
      letterSpacing: ".09em",
      textTransform: "uppercase",
    },

    title: {
      margin: 0,
      fontSize: 22,
      lineHeight: 1.2,
      fontWeight: 950,
      letterSpacing: "-0.025em",
    },

    subtitle: {
      margin: "7px 0 0",
      color: "#64748B",
      fontSize: 13,
      lineHeight: 1.5,
      fontWeight: 650,
    },

    closeButton: {
      appearance: "none",
      width: 34,
      height: 34,
      padding: 0,
      border: "1px solid #CBD5E1",
      borderRadius: 10,
      background: "#FFFFFF",
      color: "#475569",
      cursor: busy ? "not-allowed" : "pointer",
      fontSize: 20,
      lineHeight: 1,
      fontWeight: 700,
      opacity: busy ? 0.55 : 1,
    },

    body: {
      display: "grid",
      gap: 16,
      padding: "18px 22px 20px",
    },

    warning: {
      display: "grid",
      gridTemplateColumns: "24px minmax(0, 1fr)",
      gap: 10,
      alignItems: "start",
      padding: "12px 13px",
      border: "1px solid #FCD34D",
      borderRadius: 14,
      background: "#FFFBEB",
      color: "#92400E",
    },

    warningIcon: {
      width: 22,
      height: 22,
      borderRadius: 999,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "#F59E0B",
      color: "#FFFFFF",
      fontSize: 12,
      fontWeight: 950,
    },

    warningTitle: {
      marginBottom: 3,
      fontSize: 12.5,
      lineHeight: 1.35,
      fontWeight: 900,
    },

    warningText: {
      margin: 0,
      fontSize: 12,
      lineHeight: 1.5,
      fontWeight: 650,
    },

    sectionTitle: {
      margin: 0,
      fontSize: 13.5,
      lineHeight: 1.35,
      fontWeight: 900,
    },

    sectionHelp: {
      margin: "4px 0 0",
      color: "#64748B",
      fontSize: 12,
      lineHeight: 1.45,
      fontWeight: 650,
    },

    formatGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
      gap: 9,
      marginTop: 10,
    },

    formatCard: (option, selected) => ({
      position: "relative",
      display: "grid",
      alignContent: "start",
      gap: 7,
      minWidth: 0,
      minHeight: 132,
      padding: "13px 12px",
      border: `1.5px solid ${
        selected ? option.tone : option.border
      }`,
      borderRadius: 14,
      background: selected ? option.soft : "#FFFFFF",
      boxShadow: selected
        ? `0 0 0 3px ${option.tone}14`
        : "0 2px 8px rgba(15, 23, 42, 0.04)",
      cursor: busy ? "not-allowed" : "pointer",
      opacity: busy ? 0.72 : 1,
      transition:
        "border-color .15s ease, background .15s ease, box-shadow .15s ease, transform .15s ease",
      boxSizing: "border-box",
    }),

    radioLine: {
      display: "flex",
      alignItems: "center",
      gap: 8,
    },

    formatRadio: {
      width: 17,
      height: 17,
      margin: 0,
      accentColor: selectedOption.tone,
      flex: "0 0 auto",
    },

    formatTitle: {
      minWidth: 0,
      fontSize: 13,
      lineHeight: 1.3,
      fontWeight: 900,
    },

    badge: (option) => ({
      justifySelf: "start",
      padding: "3px 7px",
      borderRadius: 999,
      background: option.tone,
      color: "#FFFFFF",
      fontSize: 9.5,
      lineHeight: 1.2,
      fontWeight: 900,
      whiteSpace: "nowrap",
    }),

    formatDescription: {
      margin: 0,
      color: "#64748B",
      fontSize: 11,
      lineHeight: 1.45,
      fontWeight: 650,
    },

    reviewBox: {
      padding: 14,
      border: "1px solid #99F6E4",
      borderRadius: 16,
      background:
        "linear-gradient(135deg, #F0FDFA 0%, #FFFFFF 100%)",
    },

    reviewTop: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
    },

    reviewLead: {
      minWidth: 0,
    },

    reviewTitle: {
      margin: 0,
      color: "#115E59",
      fontSize: 13.5,
      lineHeight: 1.35,
      fontWeight: 950,
    },

    reviewText: {
      margin: "4px 0 0",
      color: "#475569",
      fontSize: 11.75,
      lineHeight: 1.5,
      fontWeight: 650,
    },

    previewButton: {
      appearance: "none",
      flex: "0 0 auto",
      minWidth: 132,
      padding: "9px 12px",
      border: "1px solid #0F766E",
      borderRadius: 11,
      background: previewing ? "#CCFBF1" : "#0F766E",
      color: previewing ? "#115E59" : "#FFFFFF",
      cursor:
        busy || typeof onPreviewPdf !== "function"
          ? "not-allowed"
          : "pointer",
      fontSize: 11.75,
      lineHeight: 1.2,
      fontWeight: 900,
      opacity:
        typeof onPreviewPdf !== "function" || finalizing ? 0.55 : 1,
      boxShadow: previewing
        ? "none"
        : "0 6px 14px rgba(15, 118, 110, 0.20)",
    },

    previewMessage: {
      marginTop: 10,
      padding: "8px 10px",
      borderRadius: 10,
      background: "#ECFDF5",
      color: "#047857",
      fontSize: 11.5,
      lineHeight: 1.45,
      fontWeight: 750,
    },

    confirmation: (checked) => ({
      display: "grid",
      gridTemplateColumns: "22px minmax(0, 1fr)",
      gap: 10,
      alignItems: "start",
      padding: 13,
      border: `1.5px solid ${
        checked ? "#16A34A" : "#CBD5E1"
      }`,
      borderRadius: 14,
      background: checked ? "#F0FDF4" : "#F8FAFC",
      cursor: busy ? "not-allowed" : "pointer",
      boxShadow: checked
        ? "0 0 0 3px rgba(22, 163, 74, 0.08)"
        : "none",
      transition:
        "border-color .15s ease, background .15s ease, box-shadow .15s ease",
    }),

    checkbox: {
      width: 19,
      height: 19,
      margin: "1px 0 0",
      accentColor: "#16A34A",
      cursor: busy ? "not-allowed" : "pointer",
    },

    confirmationTitle: {
      color: "#0F172A",
      fontSize: 12.25,
      lineHeight: 1.4,
      fontWeight: 900,
    },

    required: {
      marginLeft: 5,
      color: "#DC2626",
      fontSize: 10.5,
      fontWeight: 900,
      textTransform: "uppercase",
    },

    confirmationText: {
      margin: "4px 0 0",
      color: "#64748B",
      fontSize: 11.25,
      lineHeight: 1.5,
      fontWeight: 650,
    },

    error: {
      padding: "10px 12px",
      border: "1px solid #FCA5A5",
      borderRadius: 12,
      background: "#FEF2F2",
      color: "#B91C1C",
      fontSize: 12,
      lineHeight: 1.45,
      fontWeight: 750,
    },

    footer: {
      position: "sticky",
      bottom: 0,
      zIndex: 2,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      padding: "14px 22px",
      borderTop: "1px solid #E2E8F0",
      background: "rgba(255, 255, 255, 0.96)",
      backdropFilter: "blur(8px)",
      WebkitBackdropFilter: "blur(8px)",
    },

    footerNote: {
      minWidth: 0,
      color: "#64748B",
      fontSize: 10.75,
      lineHeight: 1.4,
      fontWeight: 700,
    },

    actions: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
      gap: 9,
      flex: "0 0 auto",
    },

    cancelButton: {
      appearance: "none",
      minWidth: 88,
      padding: "10px 13px",
      border: "1px solid #CBD5E1",
      borderRadius: 11,
      background: "#FFFFFF",
      color: "#334155",
      cursor: busy ? "not-allowed" : "pointer",
      fontSize: 12,
      lineHeight: 1.2,
      fontWeight: 850,
      opacity: busy ? 0.55 : 1,
    },

    primaryButton: {
      appearance: "none",
      minWidth: 160,
      padding: "10px 14px",
      border: "1px solid #111827",
      borderRadius: 11,
      background:
        reviewConfirmed && !busy ? "#111827" : "#E2E8F0",
      color:
        reviewConfirmed && !busy ? "#FFFFFF" : "#94A3B8",
      cursor:
        reviewConfirmed && !busy
          ? "pointer"
          : "not-allowed",
      fontSize: 12,
      lineHeight: 1.2,
      fontWeight: 900,
      boxShadow:
        reviewConfirmed && !busy
          ? "0 7px 16px rgba(17, 24, 39, 0.22)"
          : "none",
    },

    responsiveCss: `
      @media (max-width: 660px) {
        .doc-finalize-modal__backdrop {
          padding: 10px !important;
        }

        .doc-finalize-modal__card {
          max-height: calc(100dvh - 20px) !important;
          border-radius: 17px !important;
        }

        .doc-finalize-modal__header {
          grid-template-columns: 40px minmax(0, 1fr) auto !important;
          gap: 10px !important;
          padding: 16px 15px 14px !important;
        }

        .doc-finalize-modal__icon {
          width: 38px !important;
          height: 38px !important;
          border-radius: 12px !important;
        }

        .doc-finalize-modal__title {
          font-size: 19px !important;
        }

        .doc-finalize-modal__body {
          padding: 15px !important;
        }

        .doc-finalize-modal__format-grid {
          grid-template-columns: minmax(0, 1fr) !important;
        }

        .doc-finalize-modal__format-card {
          min-height: 0 !important;
        }

        .doc-finalize-modal__review-top {
          align-items: stretch !important;
          flex-direction: column !important;
        }

        .doc-finalize-modal__preview-button {
          width: 100% !important;
        }

        .doc-finalize-modal__footer {
          position: static !important;
          align-items: stretch !important;
          flex-direction: column !important;
          padding: 13px 15px 15px !important;
        }

        .doc-finalize-modal__actions {
          display: grid !important;
          grid-template-columns: minmax(0, 1fr) minmax(0, 1.35fr) !important;
          width: 100% !important;
        }

        .doc-finalize-modal__actions button {
          min-width: 0 !important;
          width: 100% !important;
        }
      }

      @media (max-height: 620px) and (min-width: 661px) {
        .doc-finalize-modal__backdrop {
          align-items: flex-start !important;
          padding-top: 14px !important;
          padding-bottom: 14px !important;
        }

        .doc-finalize-modal__card {
          max-height: calc(100dvh - 28px) !important;
        }
      }
    `,
  };

  return (
    <div
      className="doc-finalize-modal__backdrop"
      style={styles.backdrop}
      onMouseDown={handleBackdropClick}
      role="presentation"
    >
      <style>{styles.responsiveCss}</style>

      <div
        className="doc-finalize-modal__card"
        style={styles.card}
        role="dialog"
        aria-modal="true"
        aria-labelledby="doc-finalize-modal-title"
        aria-describedby="doc-finalize-modal-description"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header
          className="doc-finalize-modal__header"
          style={styles.header}
        >
          <div
            className="doc-finalize-modal__icon"
            style={styles.lockIcon}
            aria-hidden="true"
          >
            ✓
          </div>

          <div style={styles.headerText}>
            <div style={styles.eyebrow}>Final review</div>

            <h3
              id="doc-finalize-modal-title"
              className="doc-finalize-modal__title"
              style={styles.title}
            >
              Finalize &amp; Export
            </h3>

            <p
              id="doc-finalize-modal-description"
              style={styles.subtitle}
            >
              Review the exact PDF layout, choose your files, and confirm
              that the document is ready to be permanently locked.
            </p>
          </div>

          <button
            type="button"
            style={styles.closeButton}
            onClick={onClose}
            disabled={busy}
            aria-label="Close finalization dialog"
            title="Close"
          >
            ×
          </button>
        </header>

        <div
          className="doc-finalize-modal__body"
          style={styles.body}
        >
          {subject ? (
            <div
              style={{
                padding: "8px 10px",
                borderRadius: 10,
                background: "#F8FAFC",
                border: "1px solid #E2E8F0",
                color: "#475569",
                fontSize: 11.5,
                lineHeight: 1.4,
                fontWeight: 750,
                overflowWrap: "anywhere",
              }}
            >
              Document:{" "}
              <strong style={{ color: "#0F172A" }}>
                {subject}
              </strong>
            </div>
          ) : null}

          <div style={styles.warning}>
            <div style={styles.warningIcon} aria-hidden="true">
              !
            </div>

            <div>
              <div style={styles.warningTitle}>
                Finalization permanently locks this revision
              </div>

              <p style={styles.warningText}>
                After finalization, this version becomes read-only. Further
                changes require creating a new editable copy.
              </p>
            </div>
          </div>

          <section aria-labelledby="doc-finalize-format-title">
            <h4
              id="doc-finalize-format-title"
              style={styles.sectionTitle}
            >
              Choose the files to create
            </h4>

            <p style={styles.sectionHelp}>
              The final PDF is always created. Word and Text add a companion
              download.
            </p>

            <div
              className="doc-finalize-modal__format-grid"
              style={styles.formatGrid}
            >
              {formatOptions.map((option) => {
                const selected = format === option.value;

                return (
                  <label
                    key={option.value}
                    className="doc-finalize-modal__format-card"
                    style={styles.formatCard(option, selected)}
                  >
                    <div style={styles.radioLine}>
                      <input
                        type="radio"
                        name="docwriter-final-export-format"
                        value={option.value}
                        checked={selected}
                        disabled={busy}
                        onChange={() => setFormat(option.value)}
                        style={{
                          ...styles.formatRadio,
                          accentColor: option.tone,
                        }}
                      />

                      <span style={styles.formatTitle}>
                        {option.title}
                      </span>
                    </div>

                    <span style={styles.badge(option)}>
                      {option.badge}
                    </span>

                    <p style={styles.formatDescription}>
                      {option.description}
                    </p>
                  </label>
                );
              })}
            </div>
          </section>

          <section style={styles.reviewBox}>
            <div
              className="doc-finalize-modal__review-top"
              style={styles.reviewTop}
            >
              <div style={styles.reviewLead}>
                <h4 style={styles.reviewTitle}>
                  Preview the exact PDF
                </h4>

                <p style={styles.reviewText}>
                  Check page breaks, margins, spacing, names, dates, tables,
                  signatures, and the final page count before locking.
                </p>
              </div>

              <button
                type="button"
                className="doc-finalize-modal__preview-button"
                style={styles.previewButton}
                onClick={handlePreview}
                disabled={
                  busy || typeof onPreviewPdf !== "function"
                }
                aria-busy={previewing}
              >
                {previewing
                  ? "Preparing preview…"
                  : "Preview PDF"}
              </button>
            </div>

            {previewMessage ? (
              <div
                style={styles.previewMessage}
                role="status"
                aria-live="polite"
              >
                {previewMessage}
              </div>
            ) : null}
          </section>

          <label style={styles.confirmation(reviewConfirmed)}>
            <input
              type="checkbox"
              checked={reviewConfirmed}
              disabled={busy}
              onChange={(event) =>
                setReviewConfirmed(event.target.checked)
              }
              style={styles.checkbox}
            />

            <span>
              <span style={styles.confirmationTitle}>
                I reviewed the latest PDF preview and confirm that this
                document is ready to finalize.
                <span style={styles.required}>Required</span>
              </span>

              <p style={styles.confirmationText}>
                I checked the document content and final layout, including
                page breaks, margins, names, dates, and formatting.
              </p>
            </span>
          </label>

          {errorMessage ? (
            <div
              style={styles.error}
              role="alert"
              aria-live="assertive"
            >
              {errorMessage}
            </div>
          ) : null}
        </div>

        <footer
          className="doc-finalize-modal__footer"
          style={styles.footer}
        >
          <div style={styles.footerNote}>
            Selected:{" "}
            <strong style={{ color: selectedOption.tone }}>
              {selectedOption.shortTitle}
            </strong>
          </div>

          <div
            className="doc-finalize-modal__actions"
            style={styles.actions}
          >
            <button
              type="button"
              style={styles.cancelButton}
              onClick={onClose}
              disabled={busy}
            >
              Cancel
            </button>

            <button
              type="button"
              style={styles.primaryButton}
              onClick={handleFinalize}
              disabled={!reviewConfirmed || busy}
              aria-busy={finalizing}
              title={
                reviewConfirmed
                  ? finalizeLabel
                  : "Confirm the PDF review before finalizing"
              }
            >
              {finalizing
                ? "Finalizing…"
                : finalizeLabel}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default DocFinalizeModal;
